import {
  ButtonHTMLAttributes,
  ElementType,
  forwardRef,
  Fragment,
  HTMLAttributeAnchorTarget,
  MouseEvent,
  ReactElement,
} from 'react';

import styled, { CSSProperties } from 'styled-components';

import { Link, LinkProps } from '@components/link';
import { Color, Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionFast } from '@utils/transitions';

import { IconProps } from '../icons';
import { Tooltip } from '../tooltip';

export type ButtonVariant =
  | 'custom'
  | 'primary'
  | 'primary-blue'
  | 'primary-red'
  | 'primary-orange'
  | 'primary-green'
  | 'indigo'
  | 'secondary'
  | 'ghost'
  | 'ghost-no-background'
  | 'ai-gradient';

const sizeMap = {
  xs: 24,
  sm: 32,
  md: 40,
};

type ButtonSize = keyof typeof sizeMap;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  iconLeft?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  isDisabled?: boolean;
  rounded?: boolean;
  isFullWidth?: boolean;
  fitContent?: boolean;
  text?: string;
  variant?: ButtonVariant;
  as?: ElementType;
  size?: ButtonSize;
  tooltip?: string;
  font?: Font;
  color?: Color;
  href?: LinkProps['to'];
  target?: HTMLAttributeAnchorTarget;
  dataCy?: string;
  dataTestid?: string;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  textStyle?: CSSProperties;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = 'primary',
    font = 'semibold14',
    iconLeft,
    iconRight,
    text,
    // TODO: Get rid of isDisabled and just use the native prop
    isDisabled,
    disabled,
    tooltip,
    color = undefined,
    rounded = false,
    isFullWidth = false,
    fitContent = false,
    as = 'button',
    style,
    children,
    dataCy,
    dataTestid,
    href,
    target,
    onClick,
    stopPropagation = false,
    preventDefault = false,
    size = 'md',
    textStyle,
    ...rest
  } = props;

  let content = (
    <StyledButton
      as={as}
      type={as === 'button' ? (props.type ?? 'button') : undefined}
      data-variant={variant}
      data-rounded={rounded}
      disabled={isDisabled || disabled}
      data-cy={dataCy}
      data-testid={dataTestid}
      data-size={size}
      data-fullwidth={isFullWidth}
      data-fitcontent={fitContent}
      ref={ref}
      style={{
        '--button-font': theme.font[font],
        '--button-color': color ? theme.color[color] : undefined,
        ...style,
      }}
      onClick={(event: MouseEvent<HTMLButtonElement>) => {
        stopPropagation && event.stopPropagation();
        preventDefault && event.preventDefault();
        onClick?.(event);
      }}
      {...rest}
    >
      {children ?? (
        <Fragment>
          {iconLeft}
          {text && <ButtonText style={textStyle}>{text}</ButtonText>}
          {iconRight}
        </Fragment>
      )}
    </StyledButton>
  );

  if (tooltip) {
    // A disabled <button> doesn't fire hover events, so wrap it so the tooltip still shows.
    content = (
      <Tooltip content={tooltip}>
        {isDisabled || disabled ? <TooltipTarget>{content}</TooltipTarget> : content}
      </Tooltip>
    );
  }

  if (href) {
    content = (
      <Link to={href} target={target}>
        {content}
      </Link>
    );
  }

  return content;
});

const ButtonText = styled.span({
  display: 'block',
  margin: `0 ${pxToRem(8)}`,
});

const TooltipTarget = styled.span({
  display: 'inline-flex',
});

const StyledButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: transitionFast('background-color', 'box-shadow', 'color'),
  font: 'var(--button-font)',
  borderRadius: pxToRem(8),
  border: 'none',
  outline: 'none',
  padding: `0 ${pxToRem(8)}`,
  '&[data-size="xs"]': {
    height: pxToRem(sizeMap.xs),
    minWidth: pxToRem(sizeMap.xs),
    borderRadius: pxToRem(6),
    padding: `0 ${pxToRem(4)}`,
  },
  '&[data-size="sm"]': {
    height: pxToRem(sizeMap.sm),
    minWidth: pxToRem(sizeMap.sm),
    borderRadius: pxToRem(6),
  },
  '&[data-size="md"]': {
    height: theme.base.height,
    minWidth: theme.base.height,
    borderRadius: `var(--button-border-radius, ${pxToRem(8)})`,
  },
  '&[data-fullwidth="true"]': {
    width: '100%',
  },
  '&[data-fitcontent="true"]': {
    width: 'fit-content',
  },
  cursor: 'pointer',
  '&, > span': {
    color: `var(--button-color, ${theme.color.white100}) !important`,
  },
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '--icon-color': theme.color.buttonIconNegative,

  svg: {
    display: 'block',
  },

  '&[data-rounded="true"]': {
    borderRadius: pxToRem(9999),
  },

  '&[data-variant="custom"]': {
    color: `var(--custom-button-color, var(--button-color))`,
    backgroundColor: `var(--custom-button-bg-color, ${theme.color.purple100})`,
    '--shadow-focus-color': `var(--custom-button-focus-shadow, var(--custom-button-bg-color))`,
  },

  '&[data-variant="primary"]': {
    backgroundColor: theme.color.buttonPrimary,
  },

  '&[data-variant="primary-blue"]': {
    backgroundColor: theme.color.blue100,
  },

  '&[data-variant="primary-red"]': {
    backgroundColor: theme.color.red100,
  },

  '&[data-variant="primary-orange"]': {
    backgroundColor: theme.color.orange100,
  },

  '&[data-variant="primary-green"]': {
    backgroundColor: theme.color.green100,
  },
  '&[data-variant="indigo"]': {
    backgroundColor: theme.color.indigo100,
    '--button-color': theme.color.white100,
  },

  '&[data-variant="secondary"]': {
    backgroundColor: theme.color.buttonSecondary,
    '--icon-color': theme.color.buttonIconPositive,
    '--button-color': theme.color.textSecondary,
  },

  '&[data-variant="ghost"]': {
    backgroundColor: 'transparent',
    '--icon-color': theme.color.buttonIconPositive,
    '--button-color': theme.color.textSecondary,
  },

  '&[data-variant="ghost-no-background"]': {
    backgroundColor: 'transparent',
    '--icon-color': theme.color.buttonIconPositive,
    '&&': {
      boxShadow: 'none',
    },
  },

  '&[data-variant="ai-gradient"]': {
    background: theme.gradient.aiSurface10,
    '--icon-color': theme.color.buttonIconPositive,
    '--button-color': theme.color.textSecondary,
  },

  '&:disabled': {
    opacity: '0.5',
    cursor: 'not-allowed',
  },

  '&:not(:disabled)': {
    '&:hover, &:active': {
      '--icon-color': theme.color.buttonIconNegativeHover,

      '&[data-variant="custom"]': {
        backgroundColor: `var(--custom-button-bg-color-hover, var(--custom-button-bg-color))`,
      },
      '&[data-variant="primary"]': { backgroundColor: theme.color.buttonPrimaryHover },
      '&[data-variant="primary-blue"]': { backgroundColor: theme.color.blue200 },
      '&[data-variant="primary-green"]': { backgroundColor: theme.color.green200 },
      '&[data-variant="primary-orange"]': { backgroundColor: theme.color.orange200 },
      '&[data-variant="primary-red"]': { backgroundColor: theme.color.red200 },
      '&[data-variant="indigo"]': { backgroundColor: theme.color.indigo200 },

      '&[data-variant="secondary"]': {
        backgroundColor: theme.color.buttonSecondaryHover,
        '--icon-color': theme.color.buttonIconPositiveHover,
      },

      '&[data-variant="ghost"]': {
        '--icon-color': theme.color.buttonIconPositiveHover,
        backgroundColor: theme.color.uiTrans10,
      },

      '&[data-variant="ghost-no-background"]': {
        '--icon-color': theme.color.buttonIconPositiveHover,
      },

      '&[data-variant="ai-gradient"]': {
        background: theme.gradient.aiSurface20,
        // Undoes the blanket negative-hover above: this surface stays light.
        '--icon-color': theme.color.buttonIconPositiveHover,
      },
    },

    '[data-headlessui-focus-visible] &:focus-visible': {
      boxShadow: theme.shadow.focus,
      '--shadow-focus-color': theme.color.border100,
    },
  },
});
